import { ActivityType } from '@/app/models/new-activity-creation-state'
import { getActivityTypes, getStoreDetails } from '@/app/redux/actions/filters'
import { FILTERS_REDUCER_NAMESPACE } from '@/app/redux/reducers/filters'
import { FiltersService } from '@/app/services/FiltersService'
import { ProjectsService } from '@/app/services/ProjectsService'
import {
    LuxIcon,
    LuxTextInput,
    LuxSelect,
    LuxAutocomplete,
    LuxRadioInput,
    LUX_INPUT_TYPE,
} from '@lux/wardrobe-lib'
import { layoutActions, SmartlyActionItems } from '@Techedge/digitalportal-shared'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import './PhotoDetailsUpload.scss'

export interface PhotoDetailsUploadStore {
    address: string
    store_name: string
    global_store_id: string
}
export interface PhotoDetailsUploadProps {
    imageId: string
    storeId?: string
    projectType?: string
    projectTitle?: string
    macroArea: string
    activityTypeList: ActivityType[]
    disclaimer?: string
    fixtureCount?: number | string
    isStore?: boolean
    getActivityTypes: (callback) => void
    onFixtureCount: (value: string) => void
    onMacroArea: (value: string) => void
    setLoading: (loading: boolean) => void
    getStoreDetails: (storeId: string, callback: (res: any) => void) => void
    setStoreId?: (value: string) => void
    setProjectId?: (value: string) => void
}

const PhotoDetailsUpload = ({
    imageId,
    storeId,
    projectType,
    projectTitle,
    macroArea,
    disclaimer,
    fixtureCount,
    activityTypeList,
    isStore,
    onFixtureCount,
    onMacroArea,
    setStoreId,
    setProjectId,
    getActivityTypes,
    setLoading,
    getStoreDetails,
}: PhotoDetailsUploadProps) => {
    const componentClass = 'smart-modals-photo-details-upload'

    const intl = useIntl()

    const [storeDetail, setStoreDetail] = useState<Record<string, PhotoDetailsUploadStore>>({})
    const [macroareaList, setMacroareaList] = useState<string[]>([])
    const [autocompleteStoreValue, setAutocompleteStoreValue] = useState<Record<string, string>>({})
    const [autocompleteStorePredictions, setAutocompleteStorePredictions] = useState([])
    const [autocompleteProjectValue, setAutocompleteProjectValue] = useState('')
    const [autocompleteProjectPredictions, setAutocompleteProjectPredictions] = useState([])
    const [associateWithProject, setAssociateWithProject] = useState('yes')
    const [localProjectType, setLocalProjectType] = useState(projectType || '')
    const [projectNameSearchLoading, setProjectNameSearchLoading] = useState(false)
    const [storeSearchLoading, setStoreSearchLoading] = useState(false)
    const [projectTypeDisabled, setProjectTypeDisabled] = useState(false)
    const abortControllerRef = useRef(null)
    const abortControllerStoreRef = useRef(null)

    const onSearchAutocompleteStore = (storeId: string) => {
        if (abortControllerStoreRef.current) {
            abortControllerStoreRef.current.abort()
        }
        const controller = new AbortController()
        abortControllerStoreRef.current = controller
        setStoreSearchLoading(true)
        FiltersService.GetStoreList({
            mode: 'standard',
            coneOfVision: true,
            tableFilters: { storeId },
            controller,
        })
            .then((res) => {
                setAutocompleteStorePredictions(res.data.stores)
                setStoreSearchLoading(false)
            })
            .catch((error) => {
                // caused by abort controller
                if (error.message !== 'canceled') {
                    console.error('abort controller')
                }
            })
    }

    const autocompleteStoreItemTemplate = (item) => {
        return <>{item.store_name}</>
    }

    const onSearchAutocompleteProject = (title: string) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
        }
        const controller = new AbortController()
        abortControllerRef.current = controller
        setProjectNameSearchLoading(true)
        ProjectsService.SearchProject(title, localProjectType, undefined, storeId, controller)
            .then((res) => {
                setAutocompleteProjectPredictions(res.data.activities)
                setProjectNameSearchLoading(false)
            })
            .catch((error) => {
                // caused by abort controller
                if (error.message !== 'canceled') {
                    console.error('abort controller')
                }
            })
    }

    const autocompleteProjectItemTemplate = (item) => {
        return <>{item.project_name}</>
    }

    const onAssociateWithProjectChange = (value: string) => {
        setAssociateWithProject(value)
        if (value === 'no') {
            setLocalProjectType('')
            setAutocompleteProjectValue('')
            setProjectId('')
        }
    }

    useEffect(() => {
        if (storeId) {
            getStoreDetails(storeId, (res) => {
                if (!!res.storeDetail) {
                    setStoreDetail((s) => ({
                        ...s,
                        [imageId]: res.storeDetail,
                    }))
                    return
                }
                setMacroareaList(res.macroareaList)
            })
        }
    }, [storeId])

    useEffect(() => {
        if (setProjectId) {
            getActivityTypes(() => setLoading(false))
        }
    }, [])

    return (
        <div className={componentClass}>
            <div className="information text-bold">
                {intl.formatMessage({
                    id: 'modals.task.photo.details.information',
                    defaultMessage: 'Information',
                })}
            </div>
            <div className="disclaimer-container">
                <LuxIcon icon="info" size={14} />
                <span className="text-mini">
                    {!disclaimer &&
                        intl.formatMessage({
                            id: 'modals.task.photo.details.disclaimer',
                            defaultMessage:
                                'Enter the info required to categorize and save your picture',
                        })}
                    {!!disclaimer && disclaimer}
                </span>
            </div>
            {isStore && (
                <div className="label-value">
                    <div className="label text-bold">
                        {intl.formatMessage({
                            id: 'modals.task.photo.details.store.name',
                            defaultMessage: 'Store Name',
                        })}
                    </div>
                    <div className="value text">{storeDetail[imageId]?.store_name}</div>
                </div>
            )}
            {!isStore && (
                <div className="label-value">
                    <LuxAutocomplete
                        label={intl.formatMessage({
                            id: 'modals.task.photo.details.store.storeId',
                            defaultMessage: 'Store ID',
                        })}
                        value={autocompleteStoreValue[imageId]}
                        predictions={autocompleteStorePredictions}
                        minDigit={2}
                        additionalProps={{
                            appendTo: 'self',
                        }}
                        mandatory
                        onChangeCallback={(v) =>
                            setAutocompleteStoreValue((s) => ({
                                ...s,
                                [imageId]: v,
                            }))
                        }
                        onClear={() => {
                            setAutocompleteStoreValue((s) => ({
                                ...s,
                                [imageId]: '',
                            }))
                            setStoreDetail((s) => ({
                                ...s,
                                [imageId]: null,
                            }))
                        }}
                        onSearchPredictions={onSearchAutocompleteStore}
                        onPredictionSelected={(value) => {
                            setStoreId(value.store_id)
                            setStoreDetail((s) => ({
                                ...s,
                                [imageId]: value,
                            }))
                            setAutocompleteStoreValue((s) => ({
                                ...s,
                                [imageId]: value.store_name,
                            }))
                        }}
                        itemTemplate={autocompleteStoreItemTemplate}
                        isLoading={storeSearchLoading}
                    />
                </div>
            )}
            <div className="label-value">
                <div className="label text-bold">
                    {intl.formatMessage({
                        id: 'modals.task.photo.details.global.store.id',
                        defaultMessage: 'Global Store ID',
                    })}
                </div>
                <div className="value text">{storeDetail[imageId]?.global_store_id}</div>
            </div>
            <div className="label-value">
                <div className="label text-bold">
                    {intl.formatMessage({
                        id: 'modals.task.photo.details.store.address',
                        defaultMessage: 'Store Address',
                    })}
                </div>
                <div className="value text">{storeDetail[imageId]?.address}</div>
            </div>
            <div className="form-container">
                {(!!fixtureCount || fixtureCount === '') && (
                    <LuxTextInput
                        label={intl.formatMessage({
                            id: 'ph2.modals.task.photo.details.fixture.count',
                            defaultMessage: 'Fixture Count',
                        })}
                        mandatory
                        onChangeCallback={onFixtureCount}
                        value={fixtureCount}
                        type={LUX_INPUT_TYPE.number}
                        isInteger
                    />
                )}
                <LuxSelect
                    label={intl.formatMessage({
                        id: 'ph2.modals.task.photo.details.macro.area',
                        defaultMessage: 'Macro area',
                    })}
                    mandatory
                    value={macroArea}
                    onChangeCallback={onMacroArea}
                    options={macroareaList}
                    disabled={!macroareaList.length}
                />
            </div>
            <div className="title">
                <h3>
                    {intl.formatMessage({
                        id: 'modals.task.photo.details.reference.project',
                        defaultMessage: 'Reference Project',
                    })}
                </h3>
            </div>
            {!setProjectId && (
                <>
                    <div className="label-value">
                        <div className="label text-bold">
                            {intl.formatMessage({
                                id: 'modals.task.photo.details.project.type',
                                defaultMessage: 'Project Type',
                            })}
                        </div>
                        <div className="value text">{localProjectType}</div>
                    </div>
                    <div className="label-value">
                        <div className="label text-bold">
                            {intl.formatMessage({
                                id: 'modals.task.photo.details.project.name',
                                defaultMessage: 'Project Name',
                            })}
                        </div>
                        <div className="value text">{projectTitle}</div>
                    </div>
                </>
            )}
            {setProjectId && (
                <>
                    <div className="label-value">
                        <LuxRadioInput
                            onChangeCallback={onAssociateWithProjectChange}
                            value={associateWithProject}
                            radioList={[
                                {
                                    value: 'yes',
                                    label: intl.formatMessage({
                                        id: 'ph2.modals.save_picture.associate.project',
                                        defaultMessage: 'Associate with a project',
                                    }),
                                },
                                {
                                    value: 'no',
                                    label: intl.formatMessage({
                                        id: 'ph2.modals.save_picture.do.not.associate.project',
                                        defaultMessage: 'No project associated',
                                    }),
                                },
                            ]}
                            name="projectVisibility"
                        />
                    </div>
                    {associateWithProject === 'yes' && (
                        <>
                            <div className="label-value">
                                <LuxSelect
                                    label={intl.formatMessage({
                                        id: 'ph2.modals.save_picture.project.type',
                                        defaultMessage: 'Project type',
                                    })}
                                    mandatory
                                    value={localProjectType}
                                    onChangeCallback={setLocalProjectType}
                                    options={(activityTypeList || {})[SmartlyActionItems.TASK]}
                                    additionalProps={{
                                        optionLabel: (type) =>
                                            intl.formatMessage({
                                                id: `ph2.modals.new.activity.type.${type?.value}`,
                                                defaultMessage: type?.value,
                                            }),
                                        optionValue: 'value',
                                    }}
                                    disabled={projectTypeDisabled}
                                />
                            </div>
                            <div className="label-value">
                                <LuxAutocomplete
                                    label={intl.formatMessage({
                                        id: 'ph2.modals.save_picture.project.name',
                                        defaultMessage: 'Project name',
                                    })}
                                    value={autocompleteProjectValue}
                                    predictions={autocompleteProjectPredictions}
                                    additionalProps={{
                                        appendTo: 'self',
                                    }}
                                    mandatory
                                    minDigit={2}
                                    onChangeCallback={setAutocompleteProjectValue}
                                    onClear={() => {
                                        setAutocompleteProjectValue('')
                                        setLocalProjectType('')
                                        setProjectTypeDisabled(false)
                                    }}
                                    onSearchPredictions={onSearchAutocompleteProject}
                                    onPredictionSelected={(value) => {
                                        setAutocompleteProjectValue(value.project_name)
                                        setLocalProjectType(value.module)
                                        setProjectId(value.id)
                                        setProjectTypeDisabled(true)
                                    }}
                                    itemTemplate={autocompleteProjectItemTemplate}
                                    isLoading={projectNameSearchLoading}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    activityTypeList: state[FILTERS_REDUCER_NAMESPACE].projectTypeOption,
})

const mapDispatchToProps = (dispatch: any) => ({
    getActivityTypes: (callback) => {
        dispatch(getActivityTypes(callback))
    },
    getStoreDetails: (storeId: string, callback: (res: any) => void) => {
        dispatch(getStoreDetails(storeId, callback))
    },
    setLoading: (loading: boolean) => dispatch(layoutActions.setModals({ props: { loading } })),
})

export default connect(mapStateToProps, mapDispatchToProps)(PhotoDetailsUpload)
